/**
 * FleetActionRow — the card-focused action row for the fleet block inside the
 * Command Center. Every button opens an EXISTING fleet panel, drawer or dialog
 * in place; nothing navigates away from the Command Center.
 *
 * Styled to match the Command Center's own action buttons (outline on white /
 * slate-900, same height and wrap behaviour).
 */
import { useTranslation } from 'react-i18next';
import {
    Plus, Layers, RefreshCw, Gift, Smartphone, CalendarClock,
    UserSquare, Compass, Shield, SlidersHorizontal, Activity,
} from 'lucide-react';
import { Button } from '../ui/button';

const BTN = 'gap-2 justify-center h-auto min-h-[44px] py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200';

const Action = ({ icon: Icon, label, onClick, testid, primary = false, iconClass = 'text-slate-600 dark:text-slate-300' }) => (
    <Button
        variant={primary ? 'default' : 'outline'}
        onClick={onClick}
        data-testid={testid}
        className={primary
            ? 'gap-2 justify-center h-auto min-h-[44px] py-2 whitespace-normal text-center leading-tight'
            : BTN}
    >
        <Icon className={`w-4 h-4 shrink-0 ${primary ? '' : iconClass}`} />
        {label}
    </Button>
);

export default function FleetActionRow({
    onIssueNew, onBulkCreate, onRefresh, onVouchers, onCardSettings,
    onSchedules, onAssign, onDestination, onMode, onBulkActions, onFleetHealth,
    showVouchers = true,
}) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-wrap gap-3" data-testid="cc-fleet-actions">
            <Action primary icon={Plus} testid="cc-fleet-act-issue-new"
                label={t('card_fleet.ops_actions.issue_new')} onClick={onIssueNew} />
            <Action icon={Layers} testid="cc-fleet-act-bulk-create"
                label={t('card_fleet.ops_actions.bulk_create')} onClick={onBulkCreate} />
            <Action icon={RefreshCw} testid="cc-fleet-act-refresh"
                label={t('common.refresh')} onClick={onRefresh} />
            {/* Vouchers is permission-gated exactly like its side-menu entry. */}
            {showVouchers && (
                <Action icon={Gift} iconClass="text-amber-500" testid="cc-fleet-act-vouchers"
                    label={t('card_fleet.ops_actions.vouchers')} onClick={onVouchers} />
            )}
            <Action icon={Smartphone} testid="cc-fleet-act-card-settings"
                label={t('card_fleet.ops_actions.card_settings')} onClick={onCardSettings} />
            <Action icon={CalendarClock} testid="cc-fleet-act-schedules"
                label={t('card_fleet.bulk.action_schedule')} onClick={onSchedules} />
            <Action icon={UserSquare} testid="cc-fleet-act-assign"
                label={t('card_fleet.ops_actions.assign_employee')} onClick={onAssign} />
            <Action icon={Compass} testid="cc-fleet-act-destination"
                label={t('card_fleet.bulk.action_routing')} onClick={onDestination} />
            <Action icon={Shield} iconClass="text-sky-500" testid="cc-fleet-act-mode"
                label={t('card_fleet.ops_actions.review_shield_mode')} onClick={onMode} />
            <Action icon={SlidersHorizontal} testid="cc-fleet-act-bulk-actions"
                label={t('card_fleet.ops_actions.bulk_actions')} onClick={onBulkActions} />
            <Action icon={Activity} iconClass="text-emerald-500" testid="cc-fleet-act-fleet-health"
                label={t('card_fleet.ops_actions.fleet_health')} onClick={onFleetHealth} />
        </div>
    );
}
